import React, { ReactElement, ReactNode, useEffect } from 'react';
import { DownOutlined } from '@ant-design/icons';
import { Dropdown, DropdownProps, MenuProps, Spin } from 'antd';
import { MenuItemType } from 'antd/lib/menu/hooks/useItems';
import Flexbox from '@common/components/Flexbox';
import { useEnhancedDropdown } from '@common/hooks/useEnhancedDropdown';
import EnhancedDropdownLayout from '@common/layouts/EnhancedDropdownLayout';
import * as Styled from './EnhancedDropdown.styled';

type ExtendedMenuItem = MenuItemType & {
  text: string;
  id: string;
};

const itemsMapper = (items: MenuItemType[]): ExtendedMenuItem[] =>
  items.map((item) => ({ ...item, id: String(item.key), text: String(item.label) }));

/**
 * EnhancedDropdown Properties.
 */
interface EnhancedDropdownProps extends DropdownProps {
  /**
   * EnhancedDropdown items.
   */
  items: MenuItemType[];
  /**
   * EnhancedDropdown Selected items , we can call it as Value.
   */
  defaultSelected?: MenuItemType[];
  /**
   * EnhancedDropdown title.
   */
  title: string;
  /**
   * EnhancedDropdown data-test-id.
   */
  dataTestId: string;
  /**
   * Title for Enhanced Dropdown.
   */
  headerText?: string;
  /**
   * Make Dropdown same as "ant-design" Dropdown.
   */
  simple?: boolean;
  /**
   *  Selected item text below title.
   */
  previewSelected?: boolean;
  /**
   *  Hide selected list at the right.
   */
  hideSelected?: boolean;
  /**
   *  Hide Search Input.
   */
  hideSearch?: boolean;
  /**
   *  Hide Cancel Button.
   */
  hideCancelButton?: boolean;
  /**
   *  Hide Seleact All and Clear all buttons.
   */
  hideSelectionActions?: boolean;
  /**
   *  Add extra handler after click on "Cancel" button
   */
  onCancel?: () => void;
  /**
   *  Add extra handler after click on "Apply" button
   */
  onApply?: (items: MenuItemType[]) => void;
  /**
   *  Loading, showing a <Spin />
   */
  loading?: boolean;
}

/**
 * Custom Enhanced Dropdown component
 */
const EnhancedDropdown: React.FC<EnhancedDropdownProps> = ({
  title,
  items,
  defaultSelected,
  simple,
  dataTestId,
  previewSelected,
  hideSelected,
  hideCancelButton,
  hideSearch,
  hideSelectionActions,
  onCancel,
  onApply,
  headerText,
  loading,
  ...otherProps
}): ReactElement => {
  const {
    isOpen,
    filteredItems,
    handleOpenChange,
    handleCancel,
    handleSearch,
    handleSelectAll,
    handleClearAll,
    handleDeleteSelected,
    handleToggleSelect,
    selectedItems,
    searchText,
    handleApply,
    setOpen,
    selectedCountText,
    setSelected,
  } = useEnhancedDropdown<ExtendedMenuItem>({
    items: itemsMapper(items),
    defaultSelected: itemsMapper(defaultSelected ?? []),
    onApply,
    onCancel,
  });

  useEffect(() => {
    setSelected(defaultSelected.map((item) => ({ ...item, id: String(item.key), text: String(item.label) })));
  }, [defaultSelected]);

  const selectedPreview = previewSelected
    ? selectedItems.length === items.length && items.length > 0
      ? 'All'
      : selectedItems.map((item) => item.label).join(' ')
    : '';

  const handleSimpleSelectClick: MenuProps['onClick'] = (info): void => {
    const { key } = info;
    const currentItem = items.find((item) => String(item.key) === String(key));

    if (currentItem) {
      setSelected([{ ...currentItem, id: String(currentItem.key), text: String(currentItem.label) }]);
      setOpen(false);
      onApply && onApply([currentItem]);
    }
  };

  const handleDropdownRender = (originNode: ReactNode): ReactNode => (
    <Styled.EnhancedDropdownMenuWrapper>{originNode}</Styled.EnhancedDropdownMenuWrapper>
  );

  return (
    <Flexbox flexDirection='column'>
      {loading ? (
        <Spin />
      ) : (
        <>
          <Dropdown
            overlayStyle={simple ? { maxHeight: 500, overflow: 'scroll' } : undefined}
            open={isOpen}
            onOpenChange={handleOpenChange}
            data-test-id={dataTestId}
            trigger={['click']}
            menu={
              simple
                ? {
                    items,
                    onClick: handleSimpleSelectClick,
                    selectedKeys: selectedItems.map((item) => String(item.key)),
                  }
                : undefined
            }
            dropdownRender={handleDropdownRender}
            overlay={
              <EnhancedDropdownLayout<ExtendedMenuItem>
                hideSelected={hideSelected}
                title={headerText || title}
                selectedText={selectedCountText}
                items={filteredItems}
                dataTestId={dataTestId}
                hideCancelButton={hideCancelButton}
                hideSelectionActions={hideSelectionActions}
                hideSearch={hideSearch}
                searchText={searchText}
                selectedItems={selectedItems}
                handleCancel={handleCancel}
                handleApply={handleApply}
                handleSelectAll={handleSelectAll}
                handleClearAll={handleClearAll}
                handleToggleSelect={handleToggleSelect}
                handleSearch={handleSearch}
                handleDeleteSelected={handleDeleteSelected}
              />
            }
            {...otherProps}>
            <Styled.EnhancedDropdownTitle>
              {title}
              <DownOutlined />
            </Styled.EnhancedDropdownTitle>
          </Dropdown>
          {previewSelected && (
            <Styled.EnhancedDropdownPreviewSelected>{selectedPreview}</Styled.EnhancedDropdownPreviewSelected>
          )}
        </>
      )}
    </Flexbox>
  );
};

export default EnhancedDropdown;
